import { CodeExtension } from "@lexical/code-core";
import { $findMatchingParent } from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_LOW,
  defineExtension,
  KEY_ENTER_COMMAND,
  safeCast,
  type LexicalExtension,
} from "lexical";
import { MarkdownShortcutsExtension } from "../markdown/extension";
import { coarsePointer } from "./pointer";

export interface ComposerSubmitConfig {
  /** A holder rather than a function so the host callback can change without rebuilding the editor. */
  submit: { current: (() => unknown) | null };
}

/**
 * Enter submits when nothing else owns the key: not during IME composition, not with Shift (soft
 * break), and only from a plain paragraph. Every other block keeps its own Enter: a list or code
 * block edits itself, and a quote or heading, which the composer cannot make but can be given by a
 * paste, continues or ends the way it does elsewhere, so the user is never stuck inside one.
 * Where the primary pointer is coarse the keyboard is on screen and has no Shift+Enter, so there
 * Enter is always the block's and the send button sends. Mod+Enter always submits. Depending on
 * the markdown and code extensions registers this listener after theirs, so "```" + Enter still
 * makes a code block instead of sending the fence.
 */
export const ComposerSubmitExtension: LexicalExtension<
  ComposerSubmitConfig,
  "z-editor/composer-submit",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/composer-submit",
  dependencies: [MarkdownShortcutsExtension, CodeExtension],
  config: safeCast<ComposerSubmitConfig>({ submit: { current: null } }),
  register: (editor, config) =>
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        const submit = config.submit.current;
        if (event === null || submit === null) return false;
        // keyCode 229 is how WebKit reports the Enter that confirms an IME candidate.
        if (event.isComposing || event.keyCode === 229 || editor.isComposing()) return false;
        if (event.shiftKey) return false;
        if (!(event.metaKey || event.ctrlKey)) {
          if (coarsePointer.get()) return false;
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;
          const block = $findMatchingParent(
            selection.anchor.getNode(),
            (node) => $isElementNode(node) && !node.isInline(),
          );
          if (!$isParagraphNode(block) || !$isRootNode(block.getParent())) return false;
        }
        event.preventDefault();
        submit();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    ),
});
